import { useEffect, useId, useRef, useState } from "react";
import { IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import type { Budget } from "model";
import { Asterisk, Badge, Card, Colors, ErrorAlert } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";

/***** Types *****/
type BudgetFieldProps = {
  budgets: Budget[];
  selectedBudgetId?: string;
  onConfirm: (budgetId: string) => void;
  isDisabled?: boolean;
  formSubmissionErrObj?: Record<string | "budget", string>;
  onFormSubmissionError?: (
    error: (error: Record<string, string>) => Record<string, string> & { budget?: string },
  ) => void;
};

/***** Component *****/
export function BudgetField(props: BudgetFieldProps) {
  const {
    budgets,
    selectedBudgetId,
    onConfirm,
    formSubmissionErrObj,
    onFormSubmissionError,
    isDisabled,
  } = props;

  /*** Hooks ***/
  const modalId = useId();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [tempSelectedBudget, setTempSelectedBudget] = useState<Budget | undefined>(() =>
    getBudgetById({ budgets, budgetId: selectedBudgetId }),
  );
  const [searchTerm, setSearchTerm] = useState("");

  /*** Constants ***/
  // Represents the selected budget from the parent, not the temporary
  // selected budget.
  const selectedBudget = getBudgetById({ budgets, budgetId: selectedBudgetId });

  // School and District Budgets filtered by search term and selected budget.
  const filteredSchoolBudgets = budgets
    .filter((budget) => budget.subscriberType === "SCHOOL")
    .filter(
      (budget) =>
        budget.id === tempSelectedBudget?.id ||
        budget.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  const filteredDistrictBudgets = budgets
    .filter((budget) => budget.subscriberType === "DISTRICT")
    .filter(
      (budget) =>
        budget.id === tempSelectedBudget?.id ||
        budget.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

  /*** Effects ***/
  /** Update the selected budget when the prop changes. */
  useEffect(() => {
    setTempSelectedBudget(getBudgetById({ budgets, budgetId: selectedBudgetId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBudgetId]);

  /*** Handlers ***/
  function updateBudgetErrorState(budgetId: string | undefined) {
    onFormSubmissionError?.((error) => ({
      ...error,
      budget: budgetId === undefined ? translate("BUDGET_FIELD_REQUIRED_ERROR") : "",
    }));
  }

  function onModalDismiss() {
    setTempSelectedBudget(selectedBudget);
    setSearchTerm("");
    // TODO: Error handling should be handled in the parent component
    updateBudgetErrorState(selectedBudgetId);

    modalRef.current?.dismiss();
  }

  function onModalConfirm() {
    if (tempSelectedBudget) {
      updateBudgetErrorState(tempSelectedBudget.id);
      onConfirm(tempSelectedBudget.id);
    }

    modalRef.current?.dismiss();
  }

  function handleBudgetChange(budgetId: string) {
    setTempSelectedBudget(getBudgetById({ budgets, budgetId }));
  }

  /*** Render ***/
  return (
    <SelectItem
      id={modalId}
      label={
        <>
          <div>
            {translate("LABEL_BUDGET")}
            <Asterisk />
          </div>
          {formSubmissionErrObj?.budget && (
            <ErrorAlert customStyles={{ fontWeight: 400, alignItems: "center" }}>
              <>{formSubmissionErrObj?.budget}</>
            </ErrorAlert>
          )}
        </>
      }
      value={selectedBudget ? budgetNameWithPrefix({ budget: selectedBudget }) : ""}
      stacked
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal trigger={modalId} ref={modalRef} backdropDismiss onDidDismiss={onModalDismiss}>
        <ModalHeader
          title={translate("LABEL_BUDGET")}
          onModalConfirm={onModalConfirm}
          onModalDismiss={onModalDismiss}
          confirmButtonText={translate("LABEL_CONFIRM_BUDGET")}
          isConfirmTextDisabled={!tempSelectedBudget}
          // Only show the search input if there are more than 1 budget
          onIonInput={budgets.length > 1 ? (e) => setSearchTerm(e?.target?.value || "") : undefined}
        />

        {/* Modal Content */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: 16,
            overflow: "auto",
            height: "100%",
            backgroundColor: Colors.white300,
          }}
        >
          {/* School Budgets */}
          {filteredSchoolBudgets.length > 0 && (
            <Card>
              {/* School Budget Header */}
              <div
                css={{
                  position: "sticky",
                  top: -16,
                  paddingTop: 16,
                  marginTop: -16,
                  backgroundColor: Colors.white200,
                  zIndex: 5,
                }}
              >
                <div
                  css={{
                    backgroundColor: Colors.teal200,
                    padding: 16,
                    borderRadius: 8,
                  }}
                >
                  <p
                    css={{ margin: 0, fontSize: 16, fontWeight: 600 }}
                    data-testid="school-budgets-header"
                  >
                    {translate("LABEL_SCHOOL_BUDGETS")}
                  </p>
                </div>
              </div>

              {/* School Budget List */}
              <IonRadioGroup value={tempSelectedBudget?.id}>
                {/* School Budget List */}
                {filteredSchoolBudgets.map((budget) => (
                  <BudgetSelectionRow
                    key={budget.id}
                    budget={budget}
                    onBudgetChange={handleBudgetChange}
                  />
                ))}
              </IonRadioGroup>
            </Card>
          )}

          {/* District Budgets */}
          {filteredDistrictBudgets.length > 0 && (
            <Card>
              {/* District Budget Header */}
              <div
                css={{
                  position: "sticky",
                  top: -16,
                  marginTop: -16,
                  paddingTop: 16,
                  backgroundColor: Colors.white200,
                  zIndex: 5,
                }}
              >
                <div
                  css={{
                    backgroundColor: Colors.teal200,
                    padding: 16,
                    borderRadius: 8,
                  }}
                >
                  <p
                    css={{ margin: 0, fontSize: 16, fontWeight: 600 }}
                    data-testid="district-budgets-header"
                  >
                    {translate("LABEL_DISTRICT_BUDGETS")}
                  </p>
                </div>
              </div>

              {/* District Budget List */}
              <IonRadioGroup value={tempSelectedBudget?.id}>
                {/* District Budget List */}
                {filteredDistrictBudgets.map((budget) => (
                  <BudgetSelectionRow
                    key={budget.id}
                    budget={budget}
                    onBudgetChange={handleBudgetChange}
                  />
                ))}
              </IonRadioGroup>
            </Card>
          )}
        </div>
      </IonModal>
    </SelectItem>
  );
}

function BudgetSelectionRow({
  budget,
  onBudgetChange,
}: {
  budget: Budget;
  onBudgetChange: (budgetId: string) => void;
}) {
  const { name, ytdSpent, limit } = budget;

  const spendPct = ytdSpent && limit ? Math.floor((ytdSpent / limit) * 100) : undefined;

  return (
    <IonRadio
      key={budget.id}
      labelPlacement="end"
      justify="start"
      value={budget.id}
      css={{
        width: "100%",
        paddingTop: 16,
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: 16,
        boxShadow: `inset 0 -1px 0 ${Colors.slate200}`,
        // Styling the label to wrap to two lines and ellipsis after the second line
        // See https://ionicframework.com/docs/api/radio#css-shadow-parts-1
        "&::part(label)": {
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          display: "-webkit-box",
          whiteSpace: "normal",
        },
        ":last-of-type": {
          paddingBottom: 0,
          boxShadow: "none",
        },
      }}
      onClick={() => onBudgetChange(budget.id)}
    >
      {spendPct && spendPct >= 100 && (
        <Badge status="danger" text={translate("BUDGETS_OVER_LIMIT")} />
      )}
      <span>{name}</span>
      {ytdSpent && limit && limit > 0 ? (
        <Badge status="light" text={`${spendPct?.toString()}% usage`}></Badge>
      ) : (
        <></>
      )}
    </IonRadio>
  );
}

/***** Helpers *****/
function getBudgetById(params: { budgets: Budget[]; budgetId?: string }) {
  const { budgets, budgetId } = params;

  return budgets.find((budget) => budget.id === budgetId);
}

/**
 * Renders the budget name with the prefix of either "School" or "District"
 * based on the subscriber type of the budget.
 */
export function budgetNameWithPrefix(params: { budget: Budget }) {
  const { budget } = params;

  if (budget.subscriberType === "SCHOOL") return `School: ${budget.name}`;
  return `District: ${budget.name}`;
}

export function filterBudgets(budgets: Budget[] = [], schoolId: string, districtId: string) {
  const schoolBudgets = budgets.filter((budget) => budget.subscriberIds.includes(schoolId));
  const districtBudgets = budgets.filter((budget) => budget.subscriberIds.includes(districtId));

  return [...schoolBudgets, ...districtBudgets];
}
